import { useEffect, useState } from 'react';

import {
  faCalendarDay,
  faCheck,
  faLink,
  faRoute,
  faTreePalm
} from '@fortawesome/pro-light-svg-icons';
import axios, { AxiosError } from 'axios';
import { noop } from 'lodash';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';

import { PlanUnitMix } from 'components/types';

import { getPlanunitCommaIds } from 'components/utils';

import {
  DefinitionCount,
  getDefintionsCount
} from 'RestAPIs/tasks/definitions';

import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';

import { FontAwesomeSvgIcon } from 'components/shared';
import { requests } from 'RestAPIs/rapini';
import { ExpenseClaimSummaryResponse } from 'generated/index';
import { TabType } from '../Home';
import { useSelector } from 'hooks';

const tasksGroups = {
  flexduty: [
    'FlexPortalMatchNotifyPlanner',
    'DutyLineUnlinkNotificationNotifyPlanner',
    'FlexPortalMatchNotifyPlannersRejectTask'
  ],
  dutyMutationEmployee: [
    'AgendaDutyLineCreateManagerApprove',
    'AgendaDutyLineEditManagerApprove'
  ],
  dutyMutationPlanner: [
    'RosterDutyLineCreateManagerApprove',
    'RosterDutyLineEditManagerApprove'
  ],
  dutyRejectedManager: [
    'RosterDutyLineCreateNotifyPlannerRejected',
    'RosterDutyLineEditNotifyPlannerRejected'
  ],
  leaveRequests: ['LeaveRequestCreateTask', 'LeaveRequestAfterSeenTask']
};

const iconsMapping = {
  flexduty: faLink,
  dutyMutationEmployee: faCalendarDay,
  dutyMutationPlanner: faCalendarDay,
  dutyRejectedManager: faCalendarDay,
  leaveRequests: faTreePalm,
  travelExpenseClaims: faRoute
};

type Group = {
  definitionKey: string;
  count: number;
};
export type TaskGroup = {
  key: string;
  items: Group[];
};

type Props = {
  setIsLoading: (state: boolean) => void;
  selectedPlanUnits: PlanUnitMix[];
  openTab: (tabType: TabType, payload?: { [key: string]: any }) => void;
};

export default function Tasks({
  setIsLoading,
  selectedPlanUnits = [],
  openTab
}: Props) {
  const { t } = useTranslation('common');
  const { hasFeatureToggleExpenseClaims } = useSelector(({ grants }) => grants);
  const [data, setData] = useState<TaskGroup[]>([]);
  const getExpenseClaimSummary = requests.getSummary;

  useEffect(() => {
    const { CancelToken } = axios;
    const source = CancelToken.source();

    (async () => {
      if (!selectedPlanUnits || selectedPlanUnits.length === 0) {
        setData([]);
        return noop;
      }
      const costCenterIds = getPlanunitCommaIds(selectedPlanUnits);
      setIsLoading(true);

      try {
        const definitions: DefinitionCount[] =
          (
            await getDefintionsCount({
              params: { costCenterIds },
              cancelToken: source.token
            })
          )?.data || [];

        const tasks = Object.keys(tasksGroups)
          .map((key) => ({
            key,
            items: tasksGroups[key]
              .map((key) =>
                definitions.find(({ definitionKey }) => definitionKey === key)
              )
              .filter((v) => v)
          }))
          .filter(({ items }) => items.length);

        let expenseClaimSummary: ExpenseClaimSummaryResponse = {
          expenseClaimsUnapprovedLastMonth: 0,
          expenseClaimsUnapprovedCurrentMonth: 0
        };

        if (hasFeatureToggleExpenseClaims) {
          expenseClaimSummary = await getExpenseClaimSummary(
            //@ts-expect-error TODO: BE should accept array of ids instead of stringified ids, or TS interface should be updated
            costCenterIds
          );
        }

        // Travel expense claims task uses different endpoint, so we add it manually
        const travelExpenseClaimsTask = {
          key: 'travelExpenseClaims',
          items: [
            {
              count: expenseClaimSummary.expenseClaimsUnapprovedLastMonth,
              definitionKey: 'expenseClaimsUnapprovedLastMonth'
            },
            {
              count: expenseClaimSummary.expenseClaimsUnapprovedCurrentMonth,
              definitionKey: 'expenseClaimsUnapprovedCurrentMonth'
            }
          ]
        };

        setData(
          hasFeatureToggleExpenseClaims
            ? [...tasks, travelExpenseClaimsTask]
            : tasks
        );
        setIsLoading(false);
      } catch (err) {
        const error = err as AxiosError;
        if (!(error && error?.message === 'unmount')) setIsLoading(false);
      }
    })();

    return () => source.cancel('unmount');
  }, [selectedPlanUnits, setIsLoading]);

  const onTaskClicked = (definitionKey: string) => {
    if (
      definitionKey === 'expenseClaimsUnapprovedLastMonth' ||
      definitionKey === 'expenseClaimsUnapprovedCurrentMonth'
    ) {
      const currentMonthStartDate = dayjs().startOf('month').toDate();
      const lastMonthStartDate = dayjs()
        .subtract(1, 'month')
        .startOf('month')
        .toDate();

      openTab(TabType.TRAVEL_EXPENSE_CLAIMS, {
        dateOfMonth:
          definitionKey === 'expenseClaimsUnapprovedLastMonth'
            ? lastMonthStartDate
            : currentMonthStartDate
      });
    } else {
      window.dojoTopic.publish(
        'setMessagesTabFilters',
        selectedPlanUnits[0].id,
        getPlanunitCommaIds(selectedPlanUnits.slice(0, 1)),
        definitionKey
      );
      openTab(TabType.MESSAGES);
    }
  };

  return (
    <div className="task-status">
      <div className="tasks-list">
        {data.map(({ key, items }) => (
          <Paper key={key}>
            <h2>{t('taskDefinitionsShort')[key]}</h2>
            <FontAwesomeSvgIcon
              icon={iconsMapping[key]}
              className="group-icon"
              fontSize="small"
            />
            <div className="task d-flex around">
              {items.map(({ definitionKey, count }) => (
                <div key={definitionKey}>
                  <Chip
                    onClick={() => onTaskClicked(definitionKey)}
                    variant="outlined"
                    label={
                      count > 0 ? (
                        count
                      ) : (
                        <FontAwesomeSvgIcon
                          icon={faCheck}
                          className="check-icon"
                        />
                      )
                    }
                  />
                  <div className="task-name">
                    {t('taskDefinitionsShort')[definitionKey]}
                  </div>
                </div>
              ))}
            </div>
          </Paper>
        ))}
      </div>
    </div>
  );
}
